import React, { useEffect, useState } from 'react'
import { UIElement } from '../../types/UIElements'
import { useContextMotifDetails } from '../../contexts/motifDetailsContext'
import { useContextPanels } from '../../contexts/panelsContext'
import routePaths from '../../constants/routePaths'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isMobile } from 'react-device-detect'
import { keywordTags } from '../../constants/tags'
import { cn } from '../../utils/cn'
import useOpenFilter from './useOpenFilter'

// keep this in sync with the transition duration in the class name below
const TRANSITION_DURATION = 500

const transitionClassName =
  '[transition-duration:500ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]'

const itemClassName = cn(
  'relative flex h-full min-w-0 shrink-0 cursor-pointer appearance-none items-center justify-center overflow-hidden text-ellipsis border-0 font-approach-regular text-[13px] uppercase leading-none tracking-[0.05em] text-white [-webkit-tap-highlight-color:transparent] [outline:none] [transition-property:width,padding,opacity,background,color] [will-change:width,padding,opacity,background,color] active:[transform:translateY(1px)] xxl:text-[19px]',
  transitionClassName,
)

const openItemClassName = 'w-fit px-[24px] py-0 xxl:px-[36px]'
const closedItemClassName = 'w-0 p-0 opacity-0'

const separatorClassName =
  'absolute bottom-[12%] left-0 top-[12%] z-[1] block w-px bg-[#a5a5a5] opacity-70'

type ItemProps = React.ComponentProps<'button'> & { active?: boolean; open?: boolean }

const Item = ({ active, open, className = '', ...props }: ItemProps) => (
  <button
    className={cn(
      itemClassName,
      {
        [openItemClassName]: open,
        'opacity-100': open,
        [closedItemClassName]: !open,
        'bg-red hover:text-white': active,
        'bg-transparent hover:text-red': !active,
      },
      className,
    )}
    {...props}
  />
)

interface Props {
  onClose?: () => void
}

const FilterBar: React.FC<Props> = ({ onClose }) => {
  const { t, i18n } = useTranslation()

  const location = useLocation()
  const { isPanelOpen, closeAll } = useContextPanels()
  const { prepareForFilter, showFilter } = useOpenFilter()
  const {
    setDetailsOpen,
    tagFilter,
    keywordFilter,
    geoFilter,
    applyGeoFilter,
    applyTagFilter,
    applyKeywordFilter,
  } = useContextMotifDetails()

  const [showCloseButton, setShowCloseButton] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const isOpen = !!(tagFilter || geoFilter || keywordFilter !== undefined)

  // Routes where FilterBar should be hidden
  const hiddenOnRoutes = [routePaths.GENERATE || '/generate']

  const pathname = location.pathname

  useEffect(() => {
    if (isOpen) {
      setShowCloseButton(true)
      setIsClosing(false)
    } else {
      setIsClosing(true)
      const timer = setTimeout(() => {
        setShowCloseButton(false)
        setIsClosing(false)
      }, TRANSITION_DURATION * 2) // delay + transition
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  if (isPanelOpen(UIElement.WELCOME) || hiddenOnRoutes.some(r => r && pathname.startsWith(r))) {
    return null
  }

  const toggleTagFilter = () => {
    prepareForFilter()
    if (tagFilter) {
      applyTagFilter(undefined)
      window.history.replaceState(null, '', '/')
      closeAll()
    } else {
      const opening = !isPanelOpen(UIElement.TAGFILTER)
      window.history.replaceState(null, '', opening ? routePaths.THEME : '/')
      if (opening) showFilter(UIElement.TAGFILTER)
      else closeAll()
    }
  }

  const toggleKeywordFilter = () => {
    prepareForFilter()
    if (keywordFilter !== undefined) {
      applyKeywordFilter(undefined)
      window.history.replaceState(null, '', '/')
      closeAll()
    } else {
      const opening = !isPanelOpen(UIElement.KEYWORDFILTER)
      window.history.replaceState(null, '', opening ? routePaths.TAGS : '/')
      if (opening) showFilter(UIElement.KEYWORDFILTER)
      else closeAll()
    }
  }

  const toggleGeoFilter = () => {
    prepareForFilter()
    if (geoFilter) {
      applyGeoFilter(undefined)
      window.history.replaceState(null, '', '/')
      closeAll()
    } else {
      const opening = !isPanelOpen(UIElement.GEOFILTER)
      window.history.replaceState(null, '', opening ? routePaths.LOCATION : '/')
      if (opening) showFilter(UIElement.GEOFILTER)
      else closeAll()
    }
  }

  const clearFilters = () => {
    setDetailsOpen(false)
    clearFilterBar(closeAll, applyTagFilter, applyGeoFilter, applyKeywordFilter)
  }

  if (isMobile && window.innerHeight < 600) return null

  // Determine which filter is open or active
  const isThemeFilterOpen =
    isPanelOpen(UIElement.TAGFILTER) || (!!tagFilter && !geoFilter && keywordFilter === undefined)
  const isGeoFilterOpen =
    isPanelOpen(UIElement.GEOFILTER) || (!!geoFilter && !tagFilter && keywordFilter === undefined)
  const isKeywordFilterOpen =
    isPanelOpen(UIElement.KEYWORDFILTER) ||
    (keywordFilter !== undefined && !tagFilter && !geoFilter)

  const handleTagFilterClick = () => {
    if (isThemeFilterOpen && tagFilter) {
      applyTagFilter(undefined)
      showFilter(UIElement.TAGFILTER)
      setDetailsOpen(false)
      return
    }
    if (isThemeFilterOpen && !tagFilter) {
      window.history.replaceState(null, '', '/')
      closeAll()
      setDetailsOpen(false)
      return
    }
    setDetailsOpen(false)
    toggleTagFilter()
    onClose?.()
  }

  const handleKeywordFilterClick = () => {
    if (isKeywordFilterOpen && keywordFilter !== undefined) {
      applyKeywordFilter(undefined)
      showFilter(UIElement.KEYWORDFILTER)
      setDetailsOpen(false)
      return
    }
    if (isKeywordFilterOpen && keywordFilter === undefined) {
      window.history.replaceState(null, '', '/')
      closeAll()
      setDetailsOpen(false)
      return
    }
    setDetailsOpen(false)
    toggleKeywordFilter()
    onClose?.()
  }

  const handleGeoFilterClick = () => {
    if (isGeoFilterOpen && geoFilter) {
      applyGeoFilter(undefined)
      showFilter(UIElement.GEOFILTER)
      setDetailsOpen(false)
      return
    }
    if (isGeoFilterOpen && !geoFilter) {
      window.history.replaceState(null, '', '/')
      closeAll()
      setDetailsOpen(false)
      return
    }
    setDetailsOpen(false)
    toggleGeoFilter()
    onClose?.()
  }

  const selectedKeyword =
    keywordFilter !== undefined
      ? keywordTags.find(keyword => keyword.id === keywordFilter)
      : undefined
  const selectedKeywordName = selectedKeyword
    ? `${i18n.language.startsWith('en') ? selectedKeyword.EN_Name : selectedKeyword.Name} (${
        selectedKeyword.count
      })`
    : ''

  return (
    <div
      // w-max stops the left-1/2 offset from capping the bar at half the viewport; the max widths keep
      // it centered clear of the brand logo in the bottom right corner (142px, 177px from md, 251px
      // from xl, 3vw + 220px from xxl) with a 20px gap, and the selected filter truncates beyond that
      className="absolute bottom-[20px] left-1/2 z-[4] flex h-[50px] w-max max-w-[calc(100vw-324px)] md:max-w-[calc(100vw-394px)] xl:max-w-[calc(100vw-542px)] xxl:max-w-[calc(94vw-480px)] items-center justify-between whitespace-nowrap rounded-[16px] bg-black font-approach-regular text-[13px] text-white shadow-[0_10px_24px_rgba(0,0,0,0.35),0_0_0_1px_rgba(255,255,255,0.06)] [border:#a5a5a5_1px_solid] [transform:translateX(-50%)] xxl:bottom-[6vh] xxl:h-[70px] xxl:text-[19px]"
      role="toolbar"
      aria-label="Filters"
    >
      <div className="flex h-full min-w-0 flex-1 gap-0 overflow-hidden rounded-[16px]">
        <span className="flex w-fit min-w-[40px] shrink-0 items-center overflow-hidden text-ellipsis whitespace-nowrap px-[24px] py-0 text-left font-approach-regular text-[13px] leading-none tracking-[0.05em] text-white/[0.65] xxl:px-[36px] xxl:text-[19px]">
          {t('filterBar.filteredBy')}
        </span>
        <Item
          aria-label="Geographic search"
          active={isGeoFilterOpen}
          open={!isOpen || isGeoFilterOpen}
          onClick={handleGeoFilterClick}
        >
          <span className={separatorClassName} />
          {t('filterBar.location')}
        </Item>
        <Item
          aria-label="Thematic search"
          active={isThemeFilterOpen}
          open={!isOpen || isThemeFilterOpen}
          onClick={handleTagFilterClick}
        >
          <span className={separatorClassName} />
          {t('filterBar.theme')}
        </Item>
        <Item
          aria-label="Keyword search"
          active={isKeywordFilterOpen}
          open={!isOpen || isKeywordFilterOpen}
          onClick={handleKeywordFilterClick}
        >
          <span className={separatorClassName} />
          {t('filterBar.keyword')}
        </Item>
        <button
          aria-label="Selected filter"
          className={cn(
            // transitioning the column between 0fr and 1fr animates the width to fit the content
            'relative grid h-full min-w-0 cursor-default items-center overflow-hidden border-0 text-left font-approach-regular text-[13px] leading-none tracking-[0.05em] [outline:none] [transition-property:grid-template-columns,padding,background,color] [will-change:grid-template-columns,padding,background,color] xxl:text-[19px]',
            transitionClassName,
            {
              'grid-cols-[1fr] bg-white px-[24px] py-0 text-red xxl:px-[36px]': isOpen,
              'grid-cols-[0fr] bg-transparent p-0 text-transparent': !isOpen,
            },
          )}
          tabIndex={-1}
          disabled
        >
          {isOpen && (
            <span className="truncate uppercase leading-normal">
              {keywordFilter !== undefined
                ? selectedKeywordName
                : t(`${tagFilter ? 'tagFilter' : 'geoFilter'}.${tagFilter || geoFilter}`)}
            </span>
          )}
        </button>
        {showCloseButton && (
          <button
            aria-label="Close filters"
            className={cn(
              itemClassName,
              'bg-transparent hover:text-red disabled:cursor-default disabled:bg-transparent disabled:!opacity-0',
              {
                [openItemClassName]: !isClosing,
                'pointer-events-auto opacity-90': !isClosing,
                [closedItemClassName]: isClosing,
                'pointer-events-none': isClosing,
              },
            )}
            onClick={() => {
              clearFilters()
            }}
          >
            <img
              className="block size-[13px] xxl:size-[19px]"
              src="/icons/filterbar-close.svg"
              alt="close"
            />
          </button>
        )}
      </div>
    </div>
  )
}

export default FilterBar

// Export a function to clear filters and reset FilterBar state
export function clearFilterBar(
  closeAll: () => void,
  applyTagFilter: (v: undefined) => void,
  applyGeoFilter: (v: undefined) => void,
  applyKeywordFilter: (v: undefined) => void,
) {
  applyTagFilter(undefined)
  applyGeoFilter(undefined)
  applyKeywordFilter(undefined)
  closeAll()
}
